import React from 'react';
import { Box, Typography, Card, Chip, Container } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import TopNavBar from '../../../Components/TopNavigation/TopNavBar';
import { isValidTabKey } from '../../../Utils/tabNavigation';

interface UserInfo {
  name: string;
  age: number;
  gender: string;
  contact: string;
}

interface Booking {
  bookingId: string;
  name: string;
  maleCount: number;
  femaleCount: number;
  info: UserInfo[];
}

interface Batch {
  _id: string;
  date: {
    from: string;
    to: string;
  };
  bookedCount: number;
  maleCount: number;
  femaleCount: number;
  bookings: Booking[];
}

interface TripData {
  _id: string;
  title: string;
  files: Array<{
    fileKey: string;
    fileType: string;
    featured: boolean;
    fileURL: string;
  }>;
  location: {
    lat: number;
    lng: number;
    name: string;
  };
  upcomingBookingCount: number;
  batches: Batch[];
  type?: any
  totalPurchases?: any
  purchasedUser?: any
}

const BookingDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tripData = location.state?.tripData as TripData | undefined;
  const fromTab = location.state?.fromTab as string | undefined;

  const handleBackNavigation = () => {
    if (fromTab && isValidTabKey(fromTab)) {
      navigate(`/trips#${fromTab}`);
      return;
    }
    navigate(-1);
  };

  if (!tripData) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6">No trip data available</Typography>
        <Typography variant="body2" color="text.secondary">
          Please go back and select a trip from the Bookings tab
        </Typography>
        <Box
          onClick={handleBackNavigation}
          sx={{
            cursor: 'pointer',
            px: 3,
            py: 1,
            backgroundColor: '#008387',
            color: 'white',
            borderRadius: 2,
            '&:hover': { backgroundColor: '#006d70' }
          }}
        >
          Go Back
        </Box>
      </Box>
    );
  }

  const type = tripData.type;
  const batches = tripData.batches || [];
  // Filter out batches with no bookings
  const batchesWithBookings = batches.filter(batch =>
    batch.bookedCount > 0 || (batch.bookings && batch.bookings.length > 0)
  );

  if ((!batchesWithBookings || batchesWithBookings.length === 0) && type === 'trip') {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="h6">No booking data available </Typography>
      </Box>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const formatDateRange = (from: string, to: string) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);

    const fromFormatted = fromDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });

    const toFormatted = toDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });

    return `${fromFormatted} - ${toFormatted}`;
  };

  const handleBookingCardClick = (booking: Booking, batch: Batch) => {
    navigate('/trip/bookinginfo', {
      state: {
        bookingData: {
          ...tripData,
          ...booking,
          batches: batch,
          quantity: booking.info?.length || 0,
          tripId: tripData._id,
          bookingId: booking.bookingId
        },
        fromTab: fromTab || 'bookings',
      }
    });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'white',
      pb: 2
    }}>
      {/* Top Bar */}
      <TopNavBar title='  Booking details' canGoBack onBackClick={handleBackNavigation} />
      {/* Upcoming Bookings Summary */}
      <Box sx={{
        backgroundColor: '#9FA40029',
        // mx: 2, 
        mt: 2,
        p: 2,
        borderRadius: 2,
        textAlign: '-moz-left'
      }}>
        {type === 'trip' ?
          <Typography variant="body1" sx={{ color: '#757575', fontWeight: 500, fontSize: '16px' }}>
            Upcoming booking: {tripData?.upcomingBookingCount || 0}
          </Typography>
          :

          <Typography variant="body1" sx={{ color: '#757575', fontWeight: 500, fontSize: '16px' }}>
            Total Purchase: {tripData?.totalPurchases || 0}
          </Typography>
        }
      </Box>

      <Box maxWidth="sm" sx={{ mt: 2, backgroundColor: 'white' }}>
        {(tripData.purchasedUser || []).map((item: any) => {
          return (
            <Box sx={{
              p: 2,
              mb: 1,
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer'
            }}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                  Name: <span style={{ color: '#333', fontWeight: 500 }}>{item?.firstName  || 'N/A'}</span>
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                 Purchase ID: <span style={{ color: '#333', fontWeight: 500 }}>{item?._id}</span>
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                 Purchased On: <span style={{ color: '#333', fontWeight: 500 }}>{item?.purchasedAt ? formatDate(item.purchasedAt) : 'N/A'}</span>
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box maxWidth="sm" sx={{ mt: 2, backgroundColor: 'white' }}>
        {/* All Batches Section */}
        {batchesWithBookings.map((batch, batchIndex) => (
          <Box key={batch._id || batchIndex} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              mb: 2,
              color: '#333',
              textAlign: 'center'
            }}>
              {batch.date ? formatDateRange(batch.date.from, batch.date.to) : '–'}
            </Typography>

            {/* Summary Counts */}
            <Box sx={{
              display: 'flex',
              gap: 1,
              mb: 3,
              justifyContent: 'center'
            }}>
              <Chip
                // label={`Total ${batch.bookedCount || 0}`}
                label={
                  <span>
                    <span style={{ color: '#757575', fontWeight: 500 }}>Total </span>
                    <span style={{ color: '#000000', fontWeight: 600, fontSize: 20, paddingLeft: '4px', paddingTop: '4px' }}>{batch.bookedCount || 0}</span>
                  </span>
                }

                sx={{

                  backgroundColor: '#FDF6ED',
                  color: '#757575',
                  fontWeight: 500,
                  minWidth: 80,
                  paddingX: 4,
                  paddingY: 3,
                  width: '33%'

                }}
              />
              <Chip
                // label={`Male ${batch.maleCount || 0}`}
                label={
                  <span>
                    <span style={{ color: '#757575', fontWeight: 500 }}>Male </span>
                    <span style={{ color: '#000000', fontWeight: 600, fontSize: 20, paddingLeft: '4px', paddingTop: '4px' }}>{batch.maleCount || 0}</span>
                  </span>
                }
                sx={{
                  backgroundColor: '#FDF6ED',
                  color: '#757575',
                  fontWeight: 500,
                  minWidth: 80,
                  paddingX: 4,
                  paddingY: 3,
                  width: '33%'
                }}
              />
              <Chip
                label={
                  <span>
                    <span style={{ color: '#757575', fontWeight: 500 }}>Female </span>
                    <span style={{ color: '#000000', fontWeight: 600, fontSize: 20, paddingLeft: '4px', marginTop: '16px' }}>{batch.femaleCount || 0}</span>
                  </span>
                }
                sx={{
                  backgroundColor: '#FDF6ED',
                  color: '#757575',
                  fontWeight: 500,
                  minWidth: 80,
                  paddingX: 4,
                  paddingY: 3,
                  width: '33%'
                }}
              />
            </Box>

            {/* Individual Booking Entries */}
            {batch.bookings && batch.bookings.length > 0 ? (
              batch.bookings.map((booking, bookingIndex) => (
                <Box key={`${batch._id}-${bookingIndex}`} sx={{ mb: 2 }}>
                  <Box
                    onClick={() => handleBookingCardClick(booking, batch)}
                    sx={{
                      p: 2,
                      mb: 1,
                      backgroundColor: 'white',
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      cursor: 'pointer'
                    }}
                  >
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Name: <span style={{ color: '#333', fontWeight: 500 }}>{booking.name || 'N/A'}</span>
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        ID: <span style={{ color: '#333', fontWeight: 500 }}>{booking.bookingId}</span>
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Persons: <span style={{ color: '#333', fontWeight: 500 }}>{booking.info?.length || 0}</span>
                      </Typography>
                    </Box>
                    {/* <Box>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Gender: <span style={{ color: '#333', fontWeight: 500 }}>Male: {booking.maleCount || 0}, Female: {booking.femaleCount || 0}</span>
                      </Typography>
                    </Box> */}
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  No booking details available for this date range
                </Typography>
              </Box>
            )}
          </Box>
        ))}


      </Box>
    </Box>
  );
};

export default BookingDetails;
